// HUDA PORT — the cart drawer.
//
// NOT A PORT. The static site has no cart; its add-to-cart button was a stub
// that swapped a label and added nothing (quoted at hd-product-card.js:7-12).
// So there is no source behaviour to preserve here and every choice below is a
// decision. ES5 throughout — var/function, no arrow, no template literal, no
// const — matching all nine existing hd- scripts, zero exceptions.
//
// WHAT THIS FILE OWNS
//   · opening and closing the drawer, and the whole modal contract that goes
//     with it: focus move, focus trap, inert background, scroll lock, Escape,
//     backdrop click, focus restore;
//   · refreshing the drawer's contents from the Section Rendering API after a
//     mutation, and keeping the header count in step with it;
//   · the quantity stepper and remove control INSIDE the drawer.
//
// WHAT IT DELIBERATELY DOES NOT OWN: adding to cart. That still belongs to
// hd-product-card.js, which POSTs /cart/add.js and then hands the returned
// section HTML to refresh() + open() through window.HDCart.
//
// ─────────────────────────────────────────────────────────────────────────────
// THREE DEPARTURES FROM HOUSE IDIOM, each deliberate.
//
// 1. EVENT DELEGATION, not the data-*-bound MARK + init(root) idiom that
//    hd-product-card.js / hd-before-after.js / hd-marquee.js / hd-review-cards.js
//    use. Those files wire roots that ARRIVE (a theme-editor section load).
//    Here the root is permanent and only its CONTENTS are replaced, by an
//    innerHTML swap that detaches every node inside [data-hd-cart-body]. Binding
//    per-control would make every stepper and every remove button work exactly
//    once. So one click listener and one change listener sit on the drawer root,
//    which is outside the swapped region and survives every refresh. The MARK
//    idiom is still used for init() itself, so the header link can be re-wired
//    after an editor reload.
//
// 2. Escape is bound on `document`, not on the component. hd-nav.js:14 binds on
//    the header, which is right for a menu whose closed state is display:none
//    and wrong for a modal: the drawer owns the whole viewport while open, and
//    a keystroke must reach it from anywhere.
//
// 3. NO matchMedia('(prefers-reduced-motion: reduce)') GUARD, and its absence is
//    the point. hd-reveal.js:26 and hd-marquee.js:40 carry one because their CSS
//    and their JS both animate. This file never listens for transitionend and
//    never gates anything on a transition completing — see ARM SYNCHRONOUSLY
//    below — so there is nothing for a guard to protect. The reduced-motion
//    behaviour is entirely in hd-cart.css, where it kills the transitions and,
//    unlike every other hd- reduced-motion block, must NOT reset `transform`:
//    the closed state IS a transform. A guard here would be a no-op that reads
//    as coverage it does not provide.
//
// ARM SYNCHRONOUSLY. Dawn arms its focus trap inside a transitionend listener
// (cart-drawer.js:38-48). Under a `transition: none` reduced-motion block that
// event never fires, so the trap would never install and focus would never move
// — for precisely the users the setting exists to protect. Accessibility is
// never gated on motion here; open() does all of it inline.
(function () {
  'use strict';

  var SECTION = 'hd-cart-drawer'; // the section FILENAME, which is its API id
  var MARK = 'data-hdc-bound';
  var LOCK = 'hd-cart-locked';
  var SAY_DELAY = 50; // a text mutation that coalesces with the surrounding
  // subtree mutation is dropped by several screen readers

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
    'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  var root = null;
  var panel = null;
  var body = null;
  var statusNode = null;
  var errorNode = null;

  var isOpen = false;
  var lastTrigger = null;
  var busy = false;

  // Same three-line helper as hd-product-card.js:32-34, verbatim. Note it is
  // window.Shopify.routes.root and NOT window.routes — no hd- file reads the
  // latter, even though layout/theme.liquid:369 defines it for Dawn.
  function shopRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function list(sel, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(sel));
  }

  function templateName() {
    var main = document.querySelector('main[data-template]');
    return (main && main.getAttribute('data-template')) || '';
  }

  function msg(name) {
    return (root && root.getAttribute('data-msg-' + name)) || '';
  }

  // ------------------------------------------------------------- announcing
  // Blank first, then write on a later task. Both regions live OUTSIDE the
  // swapped node, so they are never destroyed and recreated pre-populated —
  // which is the one shape assistive technology reliably fails to announce.
  function say(node, text) {
    if (!node || !text) return;
    node.textContent = '';
    setTimeout(function () {
      node.textContent = text;
    }, SAY_DELAY);
  }

  // ------------------------------------------------------------ header count
  // The count is read from data-item-count on the swapped node, NEVER from a
  // /cart/add.js response: that endpoint returns line-item data only and has no
  // item_count at all, so `json.item_count` is undefined and paints the literal
  // string "undefined" into the bubble. change.js and ?sections= agree with
  // add.js on this attribute, so one code path serves all three.
  function setCount(n) {
    var count = parseInt(n, 10);
    if (isNaN(count) || count < 0) count = 0;

    list('[data-hd-cart-count]').forEach(function (el) {
      el.textContent = String(count);
      el.hidden = count === 0;
    });

    var template = msg('count');
    if (!template) return;
    list('[data-hd-cart-count-text]').forEach(function (el) {
      el.textContent = template.replace('[count]', String(count));
    });
  }

  // ---------------------------------------------------------------- focus
  function focusables() {
    return list(FOCUSABLE, panel).filter(function (el) {
      // tabindex="-1" is programmatically focusable but NOT tab-reachable, so
      // it must not set `first` or `last`. The selector alone cannot express
      // this: `a[href]` matches an anchor whatever its tabindex.
      if (el.getAttribute('tabindex') === '-1') return false;
      // offsetParent is null for display:none subtrees; the panel itself is
      // never display:none while open, so this only drops genuinely hidden
      // controls (e.g. a future collapsed block).
      return el.offsetParent !== null || el === document.activeElement;
    });
  }

  // Queried LIVE on every keystroke, never cached. Dawn's trapFocus
  // (global.js:89-92) caches first/last at call time, so after the first
  // innerHTML swap both point at detached nodes, no comparison ever matches,
  // and Tab walks straight out of a dialog the page still calls modal.
  function onTrapKey(e) {
    if (e.key !== 'Tab' || !isOpen) return;

    var items = focusables();
    if (!items.length) {
      e.preventDefault();
      panel.focus();
      return;
    }

    var first = items[0];
    var last = items[items.length - 1];
    var active = document.activeElement;

    if (e.shiftKey) {
      if (active === first || active === panel || !panel.contains(active)) {
        e.preventDefault();
        last.focus();
      }
    } else if (active === last || !panel.contains(active)) {
      e.preventDefault();
      first.focus();
    }
  }

  // Focus is keyed on a STABLE IDENTITY, never on a node — the swap detaches
  // whatever held it. data-hd-focus is "<control>:<item.key>". item.key, not an
  // index: indexes renumber when a line is removed, so an index key would
  // restore focus to the WRONG product. The attribute selector is mandatory
  // rather than stylistic — item.key contains a ':', so querySelector('#' + key)
  // throws a SyntaxError.
  function restoreFocus(key, caret) {
    var target = null;

    if (key) target = body.querySelector('[data-hd-focus="' + key + '"]');

    // the line this control belonged to is gone (a removal, or a decrement to
    // zero) — fall back to the same control on whatever line took its place,
    // then to anything focusable, then to the close button, then to the panel.
    if (!target && key) {
      var kind = key.split(':')[0];
      target = body.querySelector('[data-hd-focus^="' + kind + ':"]');
    }
    if (!target) target = body.querySelector(FOCUSABLE);
    if (!target) target = panel.querySelector('[data-hd-cart-close]');
    if (!target) target = panel;

    try {
      target.focus();
      if (caret !== null && target.setSelectionRange) target.setSelectionRange(caret, caret);
    } catch (err) {
      /* a detached or unfocusable target must never break the refresh */
    }
  }

  // ------------------------------------------------------------ background
  // aria-modal alone does NOT make the background unreachable by keyboard, and
  // inert alone says nothing to older AT. Both, or neither is enough.
  var SUPPORTS_INERT = typeof HTMLElement !== 'undefined' && 'inert' in HTMLElement.prototype;

  function setInert(on) {
    if (!SUPPORTS_INERT || !root) return;
    var kids = Array.prototype.slice.call(document.body.children);
    kids.forEach(function (el) {
      if (el === root) return;
      if (el.tagName === 'SCRIPT' || el.tagName === 'STYLE' || el.tagName === 'LINK') return;
      el.inert = on;
    });
  }

  function lockScroll(on) {
    if (on) document.documentElement.classList.add(LOCK);
    else document.documentElement.classList.remove(LOCK);
  }

  // --------------------------------------------------------------- open/close
  function closeNav() {
    // Below 720px the burger menu and this drawer can both be open, and both
    // answer Escape. The drawer is the modal, so it wins — but it closes the
    // menu on the way in rather than leaving it stacked underneath.
    list('.hd-nav.is-open').forEach(function (nav) {
      nav.classList.remove('is-open');
      var burger = nav.querySelector('.hd-nav__burger');
      if (burger) burger.setAttribute('aria-expanded', 'false');
    });
  }

  function open(trigger) {
    if (!root || !panel || isOpen) return;

    lastTrigger = trigger || document.activeElement;
    closeNav();

    root.hidden = false; // a no-op after init(); kept so open() is self-sufficient
    isOpen = true;
    root.classList.add('is-open');
    panel.setAttribute('aria-modal', 'true');

    panel.focus();
    document.addEventListener('keydown', onTrapKey, true);
    setInert(true);

    // LAST, after everything above that could throw. A lock added before a
    // throw would leave the page permanently unscrollable with no way out
    // short of a reload.
    lockScroll(true);

    // The ONE case where an open touches the network. templates/cart.json is
    // still stock Dawn, and assets/cart.js mutates the cart there with no
    // knowledge of this drawer, so on that page alone the server-rendered
    // contents can be stale. Everywhere else open() is network-free and the
    // drawer paints instantly from HTML that was already on the page.
    if (templateName() === 'cart') revalidate();
  }

  function close() {
    if (!root || !isOpen) return;

    // Both releases first: whatever else fails below, the shopper is never
    // left with a locked, inert page.
    lockScroll(false);
    setInert(false);
    document.removeEventListener('keydown', onTrapKey, true);

    isOpen = false;
    root.classList.remove('is-open');
    panel.removeAttribute('aria-modal');

    var back = lastTrigger;
    lastTrigger = null;
    if (back && back.focus) {
      try {
        back.focus();
      } catch (err) {
        /* the trigger may have been swapped out by the theme editor */
      }
    }
  }

  // --------------------------------------------------------------- refreshing
  // Swaps ONLY the innerHTML of [data-hd-cart-body]. The root, its listeners,
  // the open state and both live regions sit outside it and survive.
  function refresh(html) {
    if (!html || !root || !body) return false;

    var parsed;
    try {
      parsed = new DOMParser().parseFromString(html, 'text/html');
    } catch (err) {
      return false;
    }

    // Selected by the stable inner hook, never by `.shopify-section` or the
    // wrapper's tag name — the snippet is rendered directly in the layout and
    // through the section proxy, and neither path is required to agree about a
    // wrapper that only one of them would have.
    var next = parsed.querySelector('[data-hd-cart-body]');
    if (!next) return false;

    var active = document.activeElement;
    var inside = !!(active && root.contains(active));
    var key = inside && active.getAttribute ? active.getAttribute('data-hd-focus') : null;
    var caret = null;
    if (inside && active.tagName === 'INPUT' && typeof active.selectionStart === 'number') {
      caret = active.selectionStart;
    }

    var count = next.getAttribute('data-item-count');
    body.innerHTML = next.innerHTML;
    body.setAttribute('data-item-count', count || '0');
    setCount(count);

    // Only when focus was already in the drawer. On the add path it is on the
    // add-to-cart button of a card or the PDP, and stealing it before open()
    // has decided where focus goes would be wrong.
    if (inside) restoreFocus(key, caret);

    return true;
  }

  function setBusy(on) {
    busy = on;
    if (body) body.setAttribute('aria-busy', on ? 'true' : 'false');
  }

  // A plain GET of the section, used only where open() documents it — and
  // deliberately UNGUARDED by any once-per-view flag. On /cart the drawer can
  // go stale again after every single mutation Dawn's cart.js makes, so a
  // "already refreshed once" guard would be wrong precisely where this is used.
  // ?sections= returns { '<id>': '<html>' } — NOT nested under a `sections` key
  // the way the cart endpoints return it.
  //
  // THE PATH IS THE SHOP ROOT, NOT window.location.pathname, and that is not a
  // stylistic choice — it is the fix for a defect this call had when it was
  // written against the current path. /cart has its own JSON representation,
  // and it WINS: `GET /cart?sections=…` with Accept: application/json returns
  // the cart object (token, item_count, items…) and ignores `sections`
  // entirely. Measured. Since /cart is the one and only page this function is
  // called from, using the current path made it a guaranteed no-op exactly
  // where it was needed. The root has no JSON representation, and this section
  // reads nothing but the global `cart` object, so page context is irrelevant
  // to what comes back.
  function revalidate() {
    fetch(shopRoot() + '?sections=' + SECTION, {
      headers: { Accept: 'application/json' }
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (json) {
        refresh(json && json[SECTION]);
      })
      .catch(function () {
        /* the rendered contents stay; a stale drawer beats a broken one */
      });
  }

  // ------------------------------------------------------------- mutating
  // /cart/change.js keyed on `id` carrying the LINE ITEM KEY, not `line`.
  // `line` is a 1-based index, and an index is unsafe here: a concurrent
  // removal renumbers every line under an in-flight request. Shopify's own
  // guidance is the key for the same reason. Keys are not stable for a line's
  // lifetime — they change as the line changes — which is exactly why the whole
  // body is re-swapped after every mutation rather than one row being patched.
  function change(key, quantity, kind, onFail) {
    if (!key || busy) return;
    setBusy(true);

    fetch(shopRoot() + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: key,
        quantity: quantity,
        sections: SECTION,
        sections_url: window.location.pathname // must begin with '/', or the
        // WHOLE request 400s even though the mutation succeeded
      })
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (json) {
        if (json && json.status) throw new Error(json.description || json.message);

        // A section that fails to render comes back as null INSIDE an HTTP 200,
        // so this is never assumed present.
        var html = json && json.sections && json.sections[SECTION];
        if (!refresh(html)) throw new Error('section missing');

        setBusy(false);
        var empty = body.getAttribute('data-item-count') === '0';
        say(statusNode, empty ? msg('empty') : msg(kind === 'remove' ? 'removed' : 'updated'));
      })
      .catch(function () {
        setBusy(false);
        // Hard fallback, in the shape hd-product-card.js:104-108 established:
        // hand the browser the real navigation that completes the shopper's
        // intent. For a removal that is item.url_to_remove, which performs the
        // removal server-side and lands on /cart; for a quantity edit there is
        // no such URL, so it is /cart itself.
        if (typeof onFail === 'function') onFail();
        else window.location = shopRoot() + 'cart';
      });
  }

  // ---------------------------------------------------------------- wiring
  function readQty(input) {
    var v = parseInt(input.value, 10);
    return isNaN(v) || v < 0 ? 0 : v;
  }

  function wireDrawer(node) {
    root = node;
    panel = root.querySelector('[data-hd-cart-panel]');
    body = root.querySelector('[data-hd-cart-body]');
    statusNode = root.querySelector('[data-hd-cart-status]');
    errorNode = root.querySelector('[data-hd-cart-error]');
    if (!panel || !body) return;

    root.addEventListener('click', function (e) {
      var hit = e.target.closest
        ? e.target.closest('[data-hd-cart-close], [data-hd-cart-overlay], [data-hd-cart-qty], [data-hd-cart-remove]')
        : null;
      if (!hit || !root.contains(hit)) return;

      if (hit.hasAttribute('data-hd-cart-close') || hit.hasAttribute('data-hd-cart-overlay')) {
        e.preventDefault();
        close();
        return;
      }

      if (hit.hasAttribute('data-hd-cart-remove')) {
        e.preventDefault();
        var href = hit.getAttribute('href');
        change(hit.getAttribute('data-key'), 0, 'remove', function () {
          if (href) window.location = href;
        });
        return;
      }

      // stepper
      e.preventDefault();
      var line = hit.closest('[data-hd-cart-line]');
      var input = line && line.querySelector('[data-hd-cart-qty-input]');
      if (!input) return;
      var now = readQty(input);
      var next = hit.getAttribute('data-hd-cart-qty') === 'up' ? now + 1 : now - 1;
      if (next < 0) next = 0;
      change(hit.getAttribute('data-key'), next, next === 0 ? 'remove' : 'update');
    });

    root.addEventListener('change', function (e) {
      var input = e.target;
      if (!input.hasAttribute || !input.hasAttribute('data-hd-cart-qty-input')) return;
      var next = readQty(input);
      change(input.getAttribute('data-key'), next, next === 0 ? 'remove' : 'update');
    });

    // The drawer owns the viewport while open, so the key must reach it from
    // anywhere — hence document, not the component. Capture phase so a nested
    // widget cannot swallow it first.
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen) {
        e.preventDefault();
        close();
      }
    });

    // A lock is document state; a bfcache restore or a same-page navigation
    // must never strand it.
    window.addEventListener('pagehide', function () {
      lockScroll(false);
      setInert(false);
    });

    // Finally: the drawer becomes real. Everything above is bound, so from here
    // `hidden` can only mean "this script never ran".
    root.hidden = false;
  }

  // The header cart stays a REAL LINK to /cart and is upgraded in place — it is
  // the theme's only route there, and every fallback in the cart stack
  // (hd-product-card.js:104-108 -> form.submit() -> /cart) terminates on it. A
  // <button> would be inert through the whole defer window, on a CDN 404, and
  // with JS off, leaving a dead icon and no way to reach checkout.
  //
  // No role="button": that discards link semantics, so Space stops activating
  // natively — Dawn has to re-add a keydown handler (cart-drawer.js:20-25) to
  // repair damage it caused — and it loses middle-click, open-in-new-tab and
  // the status-bar URL. aria-haspopup="dialog" is the accurate signal, and
  // unlike aria-expanded it is static and cannot drift across the four ways
  // this drawer closes.
  //
  // The ARIA is written HERE and not in Liquid on purpose: an attribute that
  // describes JS-only behaviour lies to assistive technology on every page
  // where the JS did not run.
  function wireLink(link) {
    link.setAttribute('aria-haspopup', 'dialog');
    link.addEventListener('click', function (e) {
      // modified clicks keep their native meaning — new tab, new window,
      // download. Stripping those is the other half of why role="button" on an
      // anchor is wrong.
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      open(link);
    });
  }

  function init(node) {
    node = node || document;

    var drawers = list('[data-hd-cart]', node);
    if (node.matches && node.matches('[data-hd-cart]')) drawers.unshift(node);
    drawers.forEach(function (el) {
      if (el.hasAttribute(MARK)) return;
      el.setAttribute(MARK, '');
      wireDrawer(el);
    });

    // The header IS a section (sections/header-group.json), so an editor reload
    // replaces this link with a fresh, unwired one — the measured defect
    // sections/hd-featured-products.liquid:75-91 documents for the card forms.
    var links = list('[data-hd-cart-toggle]', node);
    if (node.matches && node.matches('[data-hd-cart-toggle]')) links.unshift(node);
    links.forEach(function (el) {
      if (el.hasAttribute(MARK)) return;
      el.setAttribute(MARK, '');
      wireLink(el);
    });
  }

  window.HDCart = {
    init: init,
    open: open,
    close: close,
    refresh: refresh,
    setCount: setCount,
    // `kind` names a data-msg-* attribute on the drawer root, so every string
    // stays in the locale files and none is ever built in JS — this codebase
    // has zero template literals and the [count] placeholder trick
    // (layout/theme.liquid:389-399 does the same for Dawn) is how it fills them.
    announce: function (kind) {
      say(statusNode, msg(kind));
    },
    fail: function () {
      say(errorNode, msg('error'));
    }
  };

  init();

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
})();
